import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  cta_text: string;
  cta_link: string;
  image_url: string;
  is_active: boolean;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  text: string;
  rating: number;
  avatar_url?: string;
  is_active: boolean;
}

export interface PlatformConfig {
  site_name: string;
  site_description: string;
  logo_url: string;
  logo_text: string;
  logo_initials: string;
  favicon_url: string;
  dashboard_icon_url: string;
  hero_slides: HeroSlide[];
  hero_badge_text: string;
  hero_cta_text: string;
  show_features_section: boolean;
  show_pricing_section: boolean;
  show_how_it_works_section: boolean;
  show_social_proof_section: boolean;
  show_cta_section: boolean;
  show_demo_link: boolean;
  testimonials: Testimonial[];
  contact_email: string;
  contact_phone: string;
  contact_whatsapp: string;
  contact_address: string;
  social_instagram: string;
  social_facebook: string;
  social_linkedin: string;
  social_twitter: string;
  social_youtube: string;
  social_tiktok: string;
  maintenance_mode: boolean;
  maintenance_message: string;
  nav_links: { label: string; href: string; is_active: boolean }[];
  footer_description: string;
  canva_api_key: string;
  canva_brand_template_url: string;
  platform_ga_measurement_id: string;
  platform_meta_pixel_id: string;
  public_chat_enabled: boolean;
  public_chat_welcome_message: string;
  evolution_api_url: string;
  evolution_api_key: string;
}

export const defaultPlatformConfig: PlatformConfig = {
  site_name: "Via Menu",
  site_description: "A plataforma mais completa para digitalizar e gerenciar seu restaurante.",
  logo_url: "",
  logo_text: "Via Menu",
  logo_initials: "VM",
  favicon_url: "",
  dashboard_icon_url: "",
  hero_slides: [
    {
      id: "1",
      title: "Digitalize seu restaurante em minutos",
      subtitle: "Cardápio digital, QR Code, delivery próprio, PDV, painel da cozinha, fidelidade e pagamentos — tudo em uma plataforma.",
      cta_text: "Começar Grátis — Sem Cartão",
      cta_link: "/cadastro",
      image_url: "",
      is_active: true,
    },
  ],
  hero_badge_text: "Plataforma #1 para Restaurantes no Brasil",
  hero_cta_text: "Começar Grátis",
  show_features_section: true,
  show_pricing_section: true,
  show_how_it_works_section: true,
  show_social_proof_section: true,
  show_cta_section: true,
  show_demo_link: true,
  testimonials: [
    { id: "1", name: "Carlos Silva", role: "Dono — Boteco do Carlos", text: "Em 2 semanas, reduzi 40% dos erros de pedido. O QR Code na mesa mudou completamente meu atendimento.", rating: 5, is_active: true },
    { id: "2", name: "Ana Beatriz", role: "Gerente — Pizzaria Napoli", text: "O delivery próprio nos economiza R$3.000/mês em taxas de marketplace. Fora a fidelização dos clientes.", rating: 5, is_active: true },
    { id: "3", name: "Roberto Mendes", role: "Chef — Bistrô Provence", text: "O painel da cozinha é genial. Os pedidos chegam em tempo real, com detalhes e prioridade. Zero confusão.", rating: 5, is_active: true },
  ],
  contact_email: "contato@viamenu.com.br",
  contact_phone: "(79) 9 9641-9162",
  contact_whatsapp: "",
  contact_address: "Serra, ES — Brasil",
  social_instagram: "",
  social_facebook: "",
  social_linkedin: "",
  social_twitter: "",
  social_youtube: "",
  social_tiktok: "",
  maintenance_mode: false,
  maintenance_message: "Estamos em manutenção. Voltamos em breve!",
  nav_links: [
    { label: "Funcionalidades", href: "/#features", is_active: true },
    { label: "Planos", href: "/#pricing", is_active: true },
    { label: "Demo", href: "/demo", is_active: true },
    { label: "Contato", href: "/contato", is_active: true },
  ],
  footer_description: "A plataforma mais completa para digitalizar e gerenciar seu restaurante.",
  canva_api_key: "",
  canva_brand_template_url: "",
  platform_ga_measurement_id: "",
  platform_meta_pixel_id: "",
  public_chat_enabled: true,
  public_chat_welcome_message: "Olá! 👋 Precisa de ajuda? Converse com um de nossos consultores!",
  evolution_api_url: "",
  evolution_api_key: "",
};

// Simple shared cache
let _sharedConfig: PlatformConfig | null = null;

export function usePlatformConfig() {
  const [config, setConfig] = useState<PlatformConfig>(_sharedConfig || defaultPlatformConfig);
  const [loading, setLoading] = useState(!_sharedConfig);

  useEffect(() => {
    if (_sharedConfig) {
      setConfig(_sharedConfig);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchConfig = async () => {
      try {
        console.log("[PlatformConfig] Starting fetch...");
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/platform_settings?key=eq.platform_config&select=key,value`,
          {
            headers: {
              "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
              "Accept": "application/json",
            },
          }
        );
        const rows = await response.json();
        console.log("[PlatformConfig] Fetch result:", { 
          status: response.status, 
          rows: rows?.length,
          logoUrl: rows?.[0]?.value?.logo_url?.substring(0, 60) || "NONE"
        });

        if (!cancelled) {
          if (rows?.[0]?.value && typeof rows[0].value === "object") {
            _sharedConfig = { ...defaultPlatformConfig, ...rows[0].value };
          } else {
            _sharedConfig = defaultPlatformConfig;
          }
          setConfig(_sharedConfig);
          setLoading(false);
        }
      } catch (e) {
        console.error("[PlatformConfig] Error:", e);
        if (!cancelled) {
          _sharedConfig = defaultPlatformConfig;
          setConfig(_sharedConfig);
          setLoading(false);
        }
      }
    };

    fetchConfig();
    return () => { cancelled = true; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/platform_settings?key=eq.platform_config&select=key,value`,
        {
          headers: {
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            "Accept": "application/json",
          },
        }
      );
      const rows = await response.json();
      if (rows?.[0]?.value && typeof rows[0].value === "object") {
        _sharedConfig = { ...defaultPlatformConfig, ...rows[0].value };
      } else {
        _sharedConfig = defaultPlatformConfig;
      }
      setConfig(_sharedConfig);
    } catch {
      // ignore
    }
  }, []);

  return { config, loading, refresh };
}

export function useAuthState() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<{ full_name: string | null } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        const { data } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("user_id", u.id)
          .maybeSingle();
        setProfile(data);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        supabase.from("profiles").select("full_name").eq("user_id", u.id).maybeSingle().then(({ data }) => setProfile(data));
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, profile, loading };
}
